import ForumOutlined from '@mui/icons-material/ForumOutlined';
import { openHelpChat, useHelpChatEnabled } from '../helpChat/HelpChat';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutline from '@mui/icons-material/HelpOutline';
import TourIcon from '@mui/icons-material/AssistantDirectionOutlined';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import PlayCircleOutline from '@mui/icons-material/PlayCircleOutline';
import SupportOutlined from '@mui/icons-material/SupportOutlined';
import { HelpTourButton, SpotlightTour, type SpotlightCloseReason, type SpotlightStep } from '@routes-plan/ui';
import { currentLang, useT, type TextKey } from '../../i18n';
import { findScreenTour, type ScreenTour } from '../../help/tours';
import GuideContent, { type HelpGuideData } from './GuideContent';
import { renderTourText } from './tourText';
import ScreenVideoPlayer, { useScreenVideoAvailable } from './ScreenVideoPlayer';

/**
 * Ayuda en cada pantalla:
 *   - Recorrido de la pantalla (`src/help/tours`): explica cada filtro, indicador, columna y botón con
 *     `SpotlightTour` de @routes-plan/ui. Arranca solo la primera vez que la persona entra a la pantalla
 *     (se recuerda en el API con `help/tours/<clave>/seen`) y después con el botón «?» del encabezado
 *     (`ScreenTourButton`) o el menú de ayuda de la barra superior (`ScreenHelpMenuButton`).
 *   - Guías por tema (`GET help/screen?path=`): el panel lateral con la guía escrita y el video. Las guías
 *     que traen `recorrido` para la pantalla también se pueden ver con el mismo recorrido.
 */

/** Paso del recorrido de una guía, tal como lo entrega el API (ruttia-platform/docs/guias). */
export interface GuideTourStep {
  screen: string;
  element: string;
  text: string;
}

export interface ScreenGuide {
  topic: string;
  title: string;
  hasVideo: boolean;
  tour: GuideTourStep[];
}

interface ScreenHelpValue {
  guides: ScreenGuide[];
  /** Abre el panel con la guía escrita (la del tema, o la primera de la pantalla). */
  openHelp: (topic?: string) => void;
  /** Recorrido de una guía por tema. */
  startTour: (topic: string) => void;
  /** Recorrido propio de la pantalla abierta, si tiene. */
  screenTour: ScreenTour | null;
  startScreenTour: () => void;
  /** Tema de la guía con video para esta pantalla, si hay. */
  videoTopic: string | null;
  /** ¿Hay video de ayuda propio de esta pantalla (`GET help/videos`) o de su guía? */
  hasVideo: boolean;
  /** Abre el video de la pantalla en el reproductor privado (o, si no tiene, el de su guía). */
  openVideo: () => void;
}

const ScreenHelpContext = createContext<ScreenHelpValue>({
  guides: [],
  openHelp: () => undefined,
  startTour: () => undefined,
  screenTour: null,
  startScreenTour: () => undefined,
  videoTopic: null,
  hasVideo: false,
  openVideo: () => undefined,
});
export const useScreenHelp = () => useContext(ScreenHelpContext);

/** La primera vez que se entra a una pantalla con recorrido, espera a que la pantalla cargue sus datos. */
const AUTO_TOUR_DELAY_MS = 1500;

interface ActiveTour {
  /** Clave con la que queda como visto. */
  key: string;
  kind: 'screen' | 'guide';
}

export const ScreenHelpProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const t = useT();
  const { pathname } = useLocation();
  const [guides, setGuides] = useState<ScreenGuide[]>([]);
  const [seen, setSeen] = useState<Set<string> | null>(null);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<HelpGuideData | null>(null);
  const [detailTopic, setDetailTopic] = useState<string | null>(null);
  const [active, setActive] = useState<ActiveTour | null>(null);
  const autoStarted = useRef<Set<string>>(new Set());
  const screenTour = useMemo(() => findScreenTour(pathname), [pathname]);
  // Video por pantalla: generado desde este mismo recorrido (ruttia-platform/tools/videos).
  const videoScreen = screenTour ? screenTour.key.replace(/^screen-/, '') : null;
  const screenVideo = useScreenVideoAvailable(videoScreen);
  const [videoOpen, setVideoOpen] = useState(false);

  // Recorridos que el usuario ya vio (pantallas y guías).
  useEffect(() => {
    axios
      .get<{ data: string[] }>('help/tours/seen')
      .then(({ data }) => setSeen(new Set(data.data)))
      .catch(() => setSeen(new Set()));
  }, []);

  // Guías de la pantalla abierta.
  useEffect(() => {
    let alive = true;
    setGuides([]);
    setOpen(false);
    setActive(null);
    axios
      .get<{ data: ScreenGuide[] }>('help/screen', { params: { path: pathname, lang: currentLang() } })
      .then(({ data }) => alive && setGuides(Array.isArray(data?.data) ? data.data : []))
      .catch(() => alive && setGuides([]));
    return () => {
      alive = false;
    };
  }, [pathname]);

  const markSeen = useCallback((key: string) => {
    setSeen((prev) => new Set<string>([...Array.from(prev ?? []), key]));
    axios.post(`help/tours/${encodeURIComponent(key)}/seen`).catch(() => undefined);
  }, []);

  const loadDetail = useCallback((topic: string) => {
    setDetailTopic(topic);
    setDetail(null);
    axios
      .get<HelpGuideData>(`help/${encodeURIComponent(topic)}`, { params: { lang: currentLang() } })
      .then(({ data }) => setDetail(data))
      .catch(() => setDetail(null));
  }, []);

  const openHelp = useCallback(
    (topic?: string) => {
      // También se usa como onClick: lo que no sea texto se ignora.
      const wanted = typeof topic === 'string' ? topic : guides[0]?.topic;
      setActive(null);
      setOpen(true);
      if (wanted && wanted !== detailTopic) loadDetail(wanted);
    },
    [guides, detailTopic, loadDetail],
  );

  const videoTopic = useMemo(() => {
    const own = screenTour?.guide && guides.find((g) => g.topic === screenTour.guide && g.hasVideo);
    return (own || guides.find((g) => g.hasVideo))?.topic ?? null;
  }, [guides, screenTour]);

  const hasVideo = screenVideo || !!videoTopic;
  const openVideo = useCallback(() => {
    if (screenVideo) {
      setActive(null);
      setOpen(false);
      setVideoOpen(true);
    } else if (videoTopic) {
      openHelp(videoTopic);
    }
  }, [screenVideo, videoTopic, openHelp]);

  const startScreenTour = useCallback(() => {
    if (!screenTour) return;
    setOpen(false);
    setActive({ key: screenTour.key, kind: 'screen' });
  }, [screenTour]);

  const startTour = useCallback(
    (topic: string) => {
      if (guides.find((g) => g.topic === topic)?.tour.length) {
        setOpen(false);
        setActive({ key: topic, kind: 'guide' });
      }
    },
    [guides],
  );

  // Arranque automático, solo la primera vez: el recorrido de la pantalla o, si no tiene, el de una guía.
  useEffect(() => {
    if (!seen || active) return undefined;
    let key: string | null = null;
    let kind: ActiveTour['kind'] = 'screen';
    if (screenTour) {
      key = screenTour.key;
    } else {
      const pending = guides.find((g) => g.tour.length && !seen.has(g.topic));
      if (pending) {
        key = pending.topic;
        kind = 'guide';
      }
    }
    const autoKey = `${key}:${pathname}`;
    if (!key || seen.has(key) || autoStarted.current.has(autoKey)) return undefined;
    const timer = window.setTimeout(() => {
      autoStarted.current.add(autoKey);
      setActive({ key: key as string, kind });
    }, AUTO_TOUR_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [guides, seen, active, pathname, screenTour]);

  // Pasos del recorrido activo, con los textos del idioma de la persona.
  const steps: SpotlightStep[] = useMemo(() => {
    if (!active) return [];
    if (active.kind === 'guide') {
      const g = guides.find((x) => x.topic === active.key);
      return (g?.tour ?? []).map((s) => ({ target: s.element, title: g?.title ?? '', body: renderTourText(s.text) }));
    }
    if (!screenTour) return [];
    const videoAction = hasVideo ? (
      <Button size="small" variant="outlined" startIcon={<PlayCircleOutline />} onClick={openVideo}>
        {t('tours.ui.watchVideo')}
      </Button>
    ) : undefined;
    const list = [...screenTour.steps, { text: 'tours.ui.end' as const }];
    return list.map((s, i) => ({
      target: 'target' in s ? s.target : undefined,
      placement: 'placement' in s ? s.placement : undefined,
      whenMissing: 'whenMissing' in s ? s.whenMissing : undefined,
      title: t(`${s.text}.title` as TextKey),
      body: renderTourText(t(`${s.text}.body` as TextKey)),
      action: i === list.length - 1 ? videoAction : undefined,
    }));
  }, [active, guides, screenTour, hasVideo, openVideo, t]);

  const onTourClose = useCallback(
    (reason: SpotlightCloseReason) => {
      // Si no había nada que mostrar (la pantalla aún no carga), no queda como visto.
      if (active && reason !== 'empty') markSeen(active.key);
      setActive(null);
    },
    [active, markSeen],
  );

  const value = useMemo(
    () => ({ guides, openHelp, startTour, screenTour, startScreenTour, videoTopic, hasVideo, openVideo }),
    [guides, openHelp, startTour, screenTour, startScreenTour, videoTopic, hasVideo, openVideo],
  );
  const current = guides.find((g) => g.topic === detailTopic);

  return (
    <ScreenHelpContext.Provider value={value}>
      {children}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        // Por encima de la barra superior (que va sobre el menú lateral), para que se vea su encabezado.
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
        PaperProps={{ sx: { width: { xs: '100vw', sm: 480, xl: 560 }, maxWidth: '100vw', bgcolor: 'background.paper' } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>
              {t('screenHelp.title')}
            </Typography>
            <IconButton aria-label={t('common.close')} onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2, minWidth: 0 }}>
            {screenTour && (
              <Button size="small" variant="contained" startIcon={<TourIcon />} sx={{ mb: 2 }} onClick={startScreenTour}>
                {t('tours.ui.menuTour')}
              </Button>
            )}
            {guides.length > 1 && (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                {guides.map((g) => (
                  <Chip
                    key={g.topic}
                    label={g.title}
                    color={g.topic === detailTopic ? 'primary' : 'default'}
                    variant={g.topic === detailTopic ? 'filled' : 'outlined'}
                    onClick={() => loadDetail(g.topic)}
                  />
                ))}
              </Stack>
            )}
            {current && (
              <>
                <Typography component="h2" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                  {current.title}
                </Typography>
                {current.tour.length > 0 && (
                  <Button size="small" variant="outlined" startIcon={<TourIcon />} sx={{ mt: 1 }} onClick={() => startTour(current.topic)}>
                    {t('screenHelp.startTour')}
                  </Button>
                )}
              </>
            )}
            {detail ? (
              <GuideContent guide={detail} compact onVideoExpired={() => detailTopic && loadDetail(detailTopic)} />
            ) : detailTopic ? (
              <>
                <Skeleton variant="rounded" height={200} sx={{ my: 2 }} />
                <Skeleton variant="text" />
                <Skeleton variant="text" width="80%" />
              </>
            ) : null}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1.5 }}>
            <Button component={RouterLink} to="/ayuda" size="small" onClick={() => setOpen(false)}>
              {t('screenHelp.allGuides')}
            </Button>
          </Box>
        </Box>
      </Drawer>
      {videoScreen && screenVideo && <ScreenVideoPlayer open={videoOpen} onClose={() => setVideoOpen(false)} screen={videoScreen} />}
      <SpotlightTour open={!!active && steps.length > 0} steps={steps} onClose={onTourClose} ariaLabel={t('screenHelp.tourLabel')} color="secondary" />
    </ScreenHelpContext.Provider>
  );
};

/** Botón «?» junto al título (prop `helpAction` de `PageHeader`): abre el recorrido de la pantalla. */
export const ScreenTourButton: React.FC = () => {
  const t = useT();
  const { screenTour, startScreenTour } = useScreenHelp();
  if (!screenTour) return null;
  return <HelpTourButton label={t('tours.ui.helpButton')} onClick={startScreenTour} data-tour="screen-help" />;
};

/**
 * Ayuda de la barra superior: un menú con el recorrido de esta pantalla, la guía escrita, el video y el
 * centro de ayuda. Si la pantalla no tiene nada propio, lleva directo al centro de ayuda.
 */
export const ScreenHelpMenuButton: React.FC = () => {
  const t = useT();
  const navigate = useNavigate();
  const { guides, openHelp, screenTour, startScreenTour, startTour, hasVideo, openVideo } = useScreenHelp();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const guideTour = guides.find((g) => g.tour.length);
  const chatEnabled = useHelpChatEnabled();
  const hasOwn = !!screenTour || guides.length > 0 || chatEnabled || hasVideo;
  const label = hasOwn ? t('screenHelp.title') : t('help.title');
  const run = (fn: () => void) => () => {
    setAnchor(null);
    fn();
  };
  return (
    <>
      <Tooltip title={label}>
        <IconButton
          aria-label={label}
          aria-haspopup={hasOwn ? 'menu' : undefined}
          onClick={(e) => (hasOwn ? setAnchor(e.currentTarget) : navigate('/ayuda'))}
        >
          <HelpOutline />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {(screenTour || guideTour) && (
          <MenuItem onClick={run(() => (screenTour ? startScreenTour() : guideTour && startTour(guideTour.topic)))}>
            <ListItemIcon>
              <TourIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('tours.ui.menuTour')} />
          </MenuItem>
        )}
        {guides.length > 0 && (
          <MenuItem onClick={run(() => openHelp())}>
            <ListItemIcon>
              <MenuBookOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('tours.ui.menuGuide')} />
          </MenuItem>
        )}
        {hasVideo && (
          <MenuItem onClick={run(openVideo)}>
            <ListItemIcon>
              <PlayCircleOutline fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('tours.ui.menuVideo')} />
          </MenuItem>
        )}
        {chatEnabled && (
          <MenuItem onClick={run(() => openHelpChat())}>
            <ListItemIcon>
              <ForumOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('helpChat.ask')} />
          </MenuItem>
        )}
        <MenuItem onClick={run(() => navigate('/ayuda'))}>
          <ListItemIcon>
            <SupportOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('tours.ui.menuCenter')} />
        </MenuItem>
      </Menu>
    </>
  );
};
